/**
 * Cover: a book cover, always 2:3.
 * kind "colour" draws the title on the book's own colours, "photo" shows an uploaded image,
 * "empty" is the dashed placeholder. Pass decorative when the title is printed next to it.
 * (cockatrice-ui `src/ui/Cover.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/cover.css';
import { mergeProps, splitProps } from 'solid-js';
import { el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface CoverProps extends Attributes {
  /** "colour" draws the title on the book's colours, "photo" shows an image, "empty" is the dashed placeholder. Inferred from src/bg when left out. */
  readonly kind?: 'colour' | 'photo' | 'empty' | undefined;
  /** sm 96×144, md 180×270, lg 220×330. Always 2:3. Default "md". */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Book title. */
  readonly title?: string | undefined;
  /** Author name. */
  readonly author?: string | undefined;
  /** Small line at the top of colour covers, e.g. "A novel". */
  readonly kicker?: string | undefined;
  /** Background colour from the book record. */
  readonly bg?: string | undefined;
  /** Text colour from the book record; at least 4.5:1 against bg. */
  readonly ink?: string | undefined;
  /** Photo URL (photo covers). */
  readonly src?: string | undefined;
  /** Accessible name; defaults to "Title, by Author". */
  readonly alt?: string | undefined;
  /** Photo covers: print the title over a bottom shade. */
  readonly overlayTitle?: boolean | undefined;
  /** Hide from assistive tech when the title is printed next to the cover. */
  readonly decorative?: boolean | undefined;
  /** Empty covers: makes "Add one" a button. */
  readonly onAdd?: ((e: MouseEvent) => void) | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
  /** Inline styles, merged after the cover's colours. */
  readonly style?: Readonly<Record<string, string>> | undefined;
}

const filled = (value: string | undefined): value is string => value !== undefined && value !== '';

export default function Cover(props: CoverProps): Child {
  const [local, rest] = splitProps(props, ['kind', 'size', 'title', 'author', 'kicker', 'bg', 'ink', 'src', 'alt', 'overlayTitle', 'decorative', 'onAdd', 'class', 'style']);
  const kind = (): 'colour' | 'photo' | 'empty' => local.kind ?? (filled(local.src) ? 'photo' : filled(local.bg) ? 'colour' : 'empty');
  const size = (): string => local.size ?? 'md';
  const label = (): string | undefined => {
    if (local.decorative === true) return undefined;
    if (filled(local.alt)) return local.alt;
    const t = uiText();
    if (filled(local.title) && filled(local.author)) return t('ui.cover.title-by', { title: local.title, author: local.author });
    if (filled(local.title)) return local.title;
    if (filled(local.author)) return t('ui.cover.by', { author: local.author });
    return t('ui.cover.label');
  };
  const title = (): Child => el('span', { class: 'ui-cover__title' }, () => local.title);
  const author = (): Child =>
    when(
      () => filled(local.author),
      () => el('span', { class: 'ui-cover__author' }, () => local.author),
    );

  return el(
    'div',
    mergeProps({
      class: () => `ui-cover ui-cover--${kind()} ui-cover--${size()}${filled(local.class) ? ` ${local.class}` : ''}`,
      role: () => (local.decorative === true ? undefined : 'img'),
      'aria-label': label,
      'aria-hidden': () => (local.decorative === true ? 'true' : undefined),
      style: () => ({ ...(kind() === 'colour' ? { '--ui-cover-bg': local.bg, '--ui-cover-ink': local.ink } : {}), ...(local.style ?? {}) }),
    }, rest),
    when(
      () => kind() === 'photo',
      () => [
        el('img', { class: 'ui-cover__img', src: () => local.src, alt: '' }),
        when(
          () => local.overlayTitle === true && filled(local.title),
          () => el('div', { class: 'ui-cover__shade' }, title(), author()),
        ),
      ],
      () =>
        when(
          () => kind() === 'colour',
          () => [
            when(
              () => filled(local.kicker),
              () => el('span', { class: 'ui-cover__kicker' }, () => local.kicker),
            ),
            title(),
            author(),
          ],
          () =>
            when(
              () => kind() === 'empty',
              () => [
                title(),
                el(
                  'span',
                  { class: 'ui-cover__hint' },
                  () => uiText()('ui.cover.none'),
                  when(
                    () => local.onAdd !== undefined,
                    () => [
                      ' · ',
                      el(
                        'button',
                        {
                          type: 'button',
                          class: 'ui-cover__add',
                          onClick: (e: MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            local.onAdd?.(e);
                          },
                        },
                        () => uiText()('ui.cover.add'),
                      ),
                    ],
                    () => () => ` · ${uiText()('ui.cover.add')}`,
                  ),
                ),
              ],
            ),
        ),
    ),
  );
}
